import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import '../EmailViewer.css';
import { apiFetch } from '../utils/api';
import { PageHeader } from '../components/Navigation';
import { buildNavState } from '../utils/navigation';

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

// Helper function to get file icon based on MIME type
const getFileIcon = (mimeType) => {
  if (!mimeType) return '📄';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('image')) return '🖼️';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return '📦';
  return '📎';
};

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Helper function to format date for ANAF display
const formatAnafDate = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString('ro-RO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest'
  });
};

// ANAF status configuration
const getAnafStatusConfig = (invoice) => {
  const { anaf_status, anaf_stare, stare_trimitere } = invoice;

  // Not sent to ANAF (stare_trimitere !== 'trimisa')
  if (stare_trimitere !== 'trimisa') {
    return {
      icon: '○',
      label: 'Nu a fost trimisă la ANAF',
      class: 'not-sent',
      showDetails: false
    };
  }

  // From here, stare_trimitere === 'trimisa' (invoice was sent)

  // Accepted by ANAF
  if (anaf_status === 'accepted' || anaf_stare === 'ok') {
    return {
      icon: '✓',
      label: 'Acceptată',
      class: 'accepted',
      showDetails: true
    };
  }

  // Rejected by ANAF
  if (anaf_status === 'rejected' || anaf_stare === 'nok') {
    return {
      icon: '✗',
      label: 'Respinsă',
      class: 'rejected',
      showDetails: true
    };
  }

  // Processing at ANAF
  if (anaf_status === 'processing' || anaf_stare === 'in prelucrare') {
    return {
      icon: '⏳',
      label: 'În procesare',
      class: 'processing',
      showDetails: true
    };
  }

  // Error during submission
  if (anaf_status === 'error') {
    return {
      icon: '⚠',
      label: 'Eroare trimitere',
      class: 'error',
      showDetails: true
    };
  }

  // Sent but no response yet (stare_trimitere === 'trimisa' but anaf_status is null/uploaded)
  // This covers: uploaded, null, or any other status after sending
  return {
    icon: '📤',
    label: 'Trimisă, în așteptare',
    class: 'uploaded',
    showDetails: true
  };
};

// Helper function to format timestamp in Romanian timezone
const formatTimestamp = (isoString) => {
  if (!isoString) return 'Data necunoscută';

  try {
    const date = new Date(isoString);

    // Always show absolute time in Romanian format
    return date.toLocaleString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Bucharest'
    });
  } catch (error) {
    return isoString;
  }
};

// Clean HTML content from email body
const cleanEmailBody = (htmlContent) => {
  if (!htmlContent) return ' ';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Remove style, script, and head tags - their CSS/JS content appears as
  // visible text when parsing full HTML documents (e.g., Outlook emails)
  const elementsToRemove = tempDiv.querySelectorAll('style, script, head');
  elementsToRemove.forEach(el => el.remove());

  // Extract text content (this automatically strips all HTML tags)
  let cleanText = tempDiv.textContent || tempDiv.innerText || '';

  // Remove excessive whitespace and newlines
  cleanText = cleanText
    .replace(/\n\s*\n\s*\n/g, '\n\n')  // Max 2 consecutive newlines
    .replace(/[ \t]+/g, ' ')            // Multiple spaces to single space
    .trim();

  return cleanText || ' ';
};

// Email viewer component
// Supports two modes:
// 1. From invoice: /invoice/:id/email - uses invoice ID to fetch email
// 2. Standalone: /email/:id - uses email ID directly
const EmailViewerPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailData, setEmailData] = useState(null);

  // Determine mode based on current path
  const isStandaloneMode = window.location.pathname.startsWith('/email/');
  const invoiceId = isStandaloneMode ? null : id;
  const emailId = isStandaloneMode ? id : null;

  // Attachments modal state
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [attachmentImages, setAttachmentImages] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100); // Zoom percentage (100% = normal)
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null); // Track which attachment was clicked
  const [reprocessing, setReprocessing] = useState(false); // Force-reprocess in flight

  useEffect(() => {
    const fetchEmailData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Choose API endpoint based on mode
        const apiUrl = isStandaloneMode
          ? `/api/emails/${emailId}`
          : `/api/invoices/${invoiceId}/email`;

        const response = await apiFetch(apiUrl);

        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.error === 'no_email') {
            setError({
              type: 'no_email',
              message: errorData.message || 'Această factură nu are email asociat.'
            });
          } else if (errorData.error === 'email_not_found') {
            setError({
              type: 'not_found',
              message: errorData.message || 'Emailul nu a fost găsit.'
            });
          } else {
            setError({
              type: 'server_error',
              message: errorData.message || 'A apărut o eroare la încărcarea emailului.'
            });
          }
          return;
        }

        const data = await response.json();
        setEmailData(data);
      } catch (err) {
        console.error('Error fetching email:', err);
        setError({
          type: 'network_error',
          message: 'Nu s-a putut conecta la server. Verificați conexiunea la internet.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEmailData();
  }, [id, isStandaloneMode, invoiceId, emailId]);

  // Zoom control functions
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 25, 25));
  const handleZoomReset = () => setZoomLevel(100);

  // Fetch and display original email attachments
  // Uses email ID directly - no invoice required
  // attachmentIndex: the index of the clicked attachment to show (0-based)
  const viewAttachments = async (attachmentIndex) => {
    // Get the email ID directly from emailData
    const targetEmailId = emailData?.id;

    if (!targetEmailId) {
      alert('Nu s-au putut identifica datele emailului.');
      return;
    }

    setSelectedAttachmentIndex(attachmentIndex);
    setLoadingAttachments(true);
    setShowAttachmentsModal(true);
    setZoomLevel(100); // Reset zoom when opening modal

    try {
      const res = await apiFetch(`/api/emails/${targetEmailId}/images`, {
        method: 'GET',
      });

      if (!res.ok) {
        throw new Error(`Eroare ${res.status}`);
      }

      const images = await res.json();
      setAttachmentImages(images);
    } catch (e) {
      console.error('Failed to fetch attachments:', e);
      setAttachmentImages([]);
      alert(`Nu s-au putut încărca atașamentele: ${e.message}`);
    } finally {
      setLoadingAttachments(false);
    }
  };

  // Build breadcrumbs based on mode
  const breadcrumbs = isStandaloneMode
    ? [
        { label: 'Validare', to: '/validation-hub' },
        { label: 'Emailuri', to: '/emails' },
        { label: 'Email Sursă', to: null }
      ]
    : [
        { label: 'Validare', to: '/validation-hub' },
        { label: 'Factura', to: `/invoice/${invoiceId}` },
        { label: 'Email Sursă', to: null }
      ];

  // Back navigation based on mode
  const backTo = isStandaloneMode ? '/emails' : `/invoice/${invoiceId}`;
  const backLabel = isStandaloneMode ? 'Înapoi la Emailuri' : 'Înapoi la Factură';

  // Loading state
  if (loading) {
    return (
      <div className="email-viewer">
        <PageHeader
          backTo={backTo}
          backLabel={backLabel}
          title="Email Sursă"
          subtitle="Se încarcă emailul..."
          breadcrumbs={breadcrumbs}
        />
        <div className="email-viewer-loading">
          <div className="email-loading-spinner"></div>
          <p className="email-loading-text">Se încarcă emailul...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="email-viewer">
        <PageHeader
          backTo={backTo}
          backLabel={backLabel}
          title="Email Sursă"
          subtitle={error.type === 'no_email' ? 'Niciun email asociat' : 'Email indisponibil'}
          breadcrumbs={breadcrumbs}
        />
        <div className="email-viewer-error">
          <div className="email-error-card">
            <div className="email-error-icon">
              {error.type === 'no_email' ? '📭' : '❌'}
            </div>
            <h2 className="email-error-title">
              {error.type === 'no_email' ? 'Niciun email asociat' : 'Email indisponibil'}
            </h2>
            <p className="email-error-message">{error.message}</p>
            <Link to={backTo} className="email-error-action">
              ← {backLabel}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Main email view
  const {
    subject,
    sender_name,
    sender_email,
    received_at,
    text_body,
    attachments,
    processing_status,
    processed_at,
    error_message,
    invoice,
    invoices,
    gmail_message_id
  } = emailData || {};

  // Simplified status mapping: success -> Procesat, ignored -> Ignorat, everything else -> Neprocesat
  const getStatusConfig = (statusValue) => {
    if (statusValue === 'success') {
      return { label: 'Procesat', class: 'email-processing-status-success', icon: '✅' };
    }
    if (statusValue === 'ignored') {
      return { label: 'Ignorat', class: 'email-processing-status-ignored', icon: '🚫' };
    }
    if (statusValue === 'duplicate') {
      return { label: 'Duplicat', class: 'email-processing-status-duplicate', icon: '⧉' };
    }
    // Everything else (pending, processing, failed, skipped, partial_success) = Neprocesat
    return { label: 'Neprocesat', class: 'email-processing-status-pending', icon: '⏳' };
  };

  const status = getStatusConfig(processing_status);

  // Build subtitle based on invoices and mode
  let subtitle;
  if (processing_status === 'ignored') {
    subtitle = 'Email ignorat (expeditor neautorizat)';
  } else if (processing_status === 'duplicate') {
    subtitle = 'Duplicat posibil — fișier identic cu unul deja facturat (vezi detaliile de mai jos)';
  } else if (processing_status === 'skipped') {
    subtitle = 'Email omis (nu a fost identificat ca factură)';
  } else if (processing_status === 'failed') {
    subtitle = 'Procesarea emailului a eșuat';
  } else if (invoices && invoices.length > 1) {
    subtitle = `Emailul din care au fost extrase ${invoices.length} facturi`;
  } else if (isStandaloneMode) {
    subtitle = invoices?.[0]?.numar_factura
      ? `Emailul din care a fost extrasă factura ${invoices[0].numar_factura}`
      : 'Email procesat';
  } else {
    subtitle = `Emailul din care a fost extrasă factura ${invoice?.numar_factura || `#${invoiceId}`}`;
  }

  // Force reprocess: accountant override for duplicate-flagged emails
  // (bypasses the SHA-256 duplicate-attachment check — detect-and-notify policy)
  const handleForceReprocess = async () => {
    if (!gmail_message_id) return;
    const confirmed = window.confirm(
      'Acest email a fost marcat ca DUPLICAT (fișier identic cu unul deja facturat).\n\n' +
      'Reprocesarea forțată va ignora această verificare și va crea o factură nouă.\n' +
      'Continuați doar dacă este o retrimitere legitimă.\n\n' +
      'Procesarea durează ~1 minut. Continuați?'
    );
    if (!confirmed) return;

    setReprocessing(true);
    try {
      const res = await apiFetch('/api/emails/process', {
        method: 'POST',
        body: JSON.stringify({ gmail_message_id, force_reprocess: true }),
      });
      const result = await res.json();
      if (!res.ok) {
        alert(`Reprocesarea a eșuat: ${result.error || 'eroare necunoscută'}`);
      } else if (result.status === 'duplicate_file') {
        alert('Emailul a fost din nou marcat ca duplicat (verificarea nu a fost ocolită).');
      } else {
        alert('Reprocesare finalizată. Pagina se va reîncărca.');
      }
      window.location.reload();
    } catch (err) {
      alert('Eroare de rețea la reprocesare. Încercați din nou.');
      setReprocessing(false);
    }
  };

  // Build action buttons for header
  const headerActions = (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
      {invoices && invoices.length > 0 && invoices.map((inv, idx) => (
        <Link key={idx} to={`/invoice/${inv.id}`} className="email-back-btn">
          📄 {inv.numar_factura}
        </Link>
      ))}
      {processing_status === 'duplicate' && (
        <button
          className="email-back-btn"
          onClick={handleForceReprocess}
          disabled={reprocessing}
          style={{ cursor: reprocessing ? 'wait' : 'pointer' }}
        >
          {reprocessing ? '⏳ Se reprocesează…' : '♻️ Reprocesează forțat'}
        </button>
      )}
    </div>
  );

  return (
    <div className="email-viewer">
      {/* Header */}
      <PageHeader
        backTo={backTo}
        backLabel={backLabel}
        title="Email Sursă"
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {/* Main content */}
      <div className="email-viewer-main">
        {/* Email card */}
        <div className="email-card">
          {/* Email header */}
          <div className="email-header">
            <div className="email-header-row">
              <div className="email-sender-avatar">
                {getInitials(sender_name)}
              </div>
              <div className="email-header-details">
                <div className="email-sender-info">
                  <span className="email-sender-name">{sender_name || 'Expeditor necunoscut'}</span>
                  <span className="email-sender-email">&lt;{sender_email || 'N/A'}&gt;</span>
                </div>
                <p className="email-recipient">
                  Către: <span>infoefactura@gmail.com</span>
                </p>
              </div>
              <div className="email-timestamp">
                {formatTimestamp(received_at)}
                {gmail_message_id && (
                  <div
                    className="email-gmail-id"
                    title="Click to copy Gmail ID"
                    onClick={() => {
                      navigator.clipboard.writeText(gmail_message_id);
                      alert('Gmail ID copied: ' + gmail_message_id);
                    }}
                    style={{
                      fontSize: '0.7rem',
                      color: 'var(--text-secondary)',
                      opacity: 0.6,
                      cursor: 'pointer',
                      marginTop: '4px',
                      fontFamily: 'monospace'
                    }}
                  >
                    ID: {gmail_message_id}
                  </div>
                )}
              </div>
            </div>
            <h2 className="email-subject-line">{subject || '(Fără subiect)'}</h2>
          </div>

          {/* Body */}
          <div className="email-body">
            <div className="email-body-content">
              {cleanEmailBody(text_body)}
            </div>
          </div>

          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <div className="email-attachments-section">
              <div className="email-attachments-header-wrapper">
                <h4 className="email-attachments-header">
                  <span className="email-attachments-icon">📎</span>
                  Atașamente Regăsite
                </h4>
                <span className="email-attachments-count">
                  {attachments.length} {attachments.length === 1 ? 'fișier' : 'fișiere'}
                </span>
              </div>
              <p className="email-attachments-description">
                Fișierele descoperite în acest email au fost procesate automat de agentul contAI pentru extragerea datelor facturii. Clic pe un atașament pentru a vedea imaginea convertită.
              </p>

              <div className="email-attachments-list">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="email-attachment-item"
                    onClick={() => viewAttachments(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="email-attachment-icon-wrapper">
                      <span className="email-attachment-file-icon">
                        {getFileIcon(attachment.mime_type)}
                      </span>
                    </div>
                    <div className="email-attachment-details">
                      <p className="email-attachment-filename">
                        {attachment.filename || 'Fișier fără nume'}
                      </p>
                      <div className="email-attachment-metadata">
                        <span className="email-attachment-size">{formatFileSize(attachment.size)}</span>
                        <span className="email-attachment-separator">•</span>
                        <span className="email-attachment-type">
                          {attachment.mime_type === 'application/pdf' ? 'PDF' :
                           attachment.mime_type?.startsWith('image/') ? 'Imagine' :
                           'Document'}
                        </span>
                        {attachment.source && (
                          <>
                            <span className="email-attachment-separator">•</span>
                            <span className="email-attachment-source">
                              {attachment.source === 'email_attachment' ? 'Atașat la email' : 'Descărcat din link'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Processing info */}
        <div className="email-processing-info">
          <div className="email-processing-header">
            <span className="email-processing-icon">ℹ️</span>
            <h3 className="email-processing-title">Informații procesare</h3>
          </div>
          <div className="email-processing-details">
            <span className="email-processing-label">Status:</span>
            <span className="email-processing-value">
              <span className={`email-processing-status ${status.class}`}>
                <span>{status.icon}</span>
                <span>{status.label}</span>
              </span>
            </span>

            <span className="email-processing-label">Procesat la:</span>
            <span className="email-processing-value">
              {processed_at ? formatTimestamp(processed_at) : 'În așteptare'}
            </span>

            <span className="email-processing-label">
              {invoices && invoices.length > 1 ? 'Facturi create:' : 'Factură creată:'}
            </span>
            <span className="email-processing-value">
              {invoices && invoices.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {invoices.map((inv, idx) => {
                    const confidenceClass = inv.confidence_flag?.toLowerCase() || 'unknown';
                    return (
                      <Link
                        key={idx}
                        to={`/invoice/${inv.id}`}
                        className={`email-invoice-badge email-invoice-badge-${confidenceClass}`}
                      >
                        {inv.numar_factura}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                'N/A'
              )}
            </span>

            {error_message && (
              <>
                <span className="email-processing-label">Eroare:</span>
                <span className="email-processing-value" style={{ color: '#dc2626' }}>
                  {error_message}
                </span>
              </>
            )}
          </div>
        </div>

        {/* ANAF e-Factura Status Section */}
        {invoices && invoices.length > 0 && (
          <div className="email-anaf-section">
            <div className="email-anaf-header">
              <span className="email-anaf-icon">📋</span>
              <h3 className="email-anaf-title">ANAF e-Factura</h3>
            </div>
            <div className="email-anaf-content">
              {invoices.map((inv, idx) => {
                const anafConfig = getAnafStatusConfig(inv);
                return (
                  <div key={idx} className={`email-anaf-item email-anaf-status-${anafConfig.class}`}>
                    <div className="email-anaf-item-header">
                      <span className={`email-anaf-status-icon email-anaf-icon-${anafConfig.class}`}>
                        {anafConfig.icon}
                      </span>
                      <span className="email-anaf-invoice-number">{inv.numar_factura}:</span>
                      <span className="email-anaf-status-label">{anafConfig.label}</span>
                    </div>

                    {anafConfig.showDetails && (
                      <div className="email-anaf-details">
                        {inv.anaf_upload_id && (
                          <div className="email-anaf-detail-row">
                            <span className="email-anaf-detail-label">Upload ID:</span>
                            <span className="email-anaf-detail-value">{inv.anaf_upload_id}</span>
                          </div>
                        )}
                        {inv.anaf_submitted_at && (
                          <div className="email-anaf-detail-row">
                            <span className="email-anaf-detail-label">Trimis la:</span>
                            <span className="email-anaf-detail-value">{formatAnafDate(inv.anaf_submitted_at)}</span>
                          </div>
                        )}
                        {inv.anaf_response_message && anafConfig.class === 'rejected' && (
                          <div className="email-anaf-detail-row email-anaf-error-row">
                            <span className="email-anaf-detail-label">Motiv respingere:</span>
                            <span className="email-anaf-detail-value">{inv.anaf_response_message}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Only show ANAF link if invoice was sent to ANAF */}
                    {anafConfig.showDetails && (
                      <Link
                        to="/anaf-invoices"
                        state={buildNavState('email-viewer', location.pathname)}
                        className="email-anaf-details-btn"
                      >
                        Vezi situație ANAF →
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Attachments Modal - shows single attachment based on clicked index */}
      {showAttachmentsModal && (
        <div
          className="attachment-modal-overlay"
          onClick={() => setShowAttachmentsModal(false)}
        >
          <div
            className="attachment-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Fixed at top */}
            <div className="attachment-modal-header">
              <div className="attachment-modal-info">
                {!loadingAttachments && attachmentImages[selectedAttachmentIndex] && (
                  <>
                    <span className="attachment-modal-filename">
                      {attachmentImages[selectedAttachmentIndex]?.filename || attachments?.[selectedAttachmentIndex]?.filename || 'Atașament'}
                    </span>
                    <span className="attachment-modal-meta">
                      {attachmentImages[selectedAttachmentIndex]?.mime_type} • Convertit în PNG
                    </span>
                  </>
                )}
              </div>

              <div className="attachment-modal-controls">
                {/* Zoom Controls */}
                <div className="attachment-zoom-controls">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 25}
                    className="attachment-zoom-btn"
                    title="Micșorează"
                  >
                    −
                  </button>
                  <span className="attachment-zoom-level">{zoomLevel}%</span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 300}
                    className="attachment-zoom-btn"
                    title="Mărește"
                  >
                    +
                  </button>
                  <button
                    onClick={handleZoomReset}
                    className="attachment-zoom-btn attachment-zoom-reset"
                    title="Resetează zoom"
                  >
                    Reset
                  </button>
                </div>

                {/* Navigation between attachments (if multiple exist) */}
                {attachmentImages.length > 1 && (
                  <div className="attachment-nav-controls">
                    <button
                      onClick={() => setSelectedAttachmentIndex(prev => Math.max(0, prev - 1))}
                      disabled={selectedAttachmentIndex === 0}
                      className="attachment-nav-btn"
                      title="Anterior"
                    >
                      ←
                    </button>
                    <span className="attachment-nav-indicator">
                      {selectedAttachmentIndex + 1} / {attachmentImages.length}
                    </span>
                    <button
                      onClick={() => setSelectedAttachmentIndex(prev => Math.min(attachmentImages.length - 1, prev + 1))}
                      disabled={selectedAttachmentIndex === attachmentImages.length - 1}
                      className="attachment-nav-btn"
                      title="Următor"
                    >
                      →
                    </button>
                  </div>
                )}

                {/* Close Button */}
                <button
                  onClick={() => setShowAttachmentsModal(false)}
                  className="attachment-modal-close"
                  title="Închide"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable image area */}
            <div className="attachment-modal-body">
              {loadingAttachments ? (
                <div className="attachment-modal-loading">
                  <div className="email-loading-spinner"></div>
                  <p>Se încarcă imaginea...</p>
                </div>
              ) : attachmentImages.length === 0 ? (
                <div className="attachment-modal-empty">
                  <p>Nu există imagini disponibile pentru acest atașament.</p>
                  <p className="attachment-modal-empty-hint">
                    Imaginile convertite sunt disponibile doar pentru emailurile procesate recent.
                  </p>
                </div>
              ) : attachmentImages[selectedAttachmentIndex] ? (
                <div className="attachment-image-wrapper">
                  <img
                    src={attachmentImages[selectedAttachmentIndex].url}
                    alt={attachmentImages[selectedAttachmentIndex].filename}
                    className="attachment-image"
                    style={{
                      transform: `scale(${zoomLevel / 100})`,
                      transformOrigin: 'top center'
                    }}
                  />
                </div>
              ) : (
                <div className="attachment-modal-empty">
                  <p>Imaginea nu a fost găsită.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailViewerPage;
